import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Modal, Alert, Spinner 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import './Produits.css';

export default function Categories() {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedCategorie, setSelectedCategorie] = useState(null);
    const [saving, setSaving] = useState(false);
    
    // États pour les alertes
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [categorieToDelete, setCategorieToDelete] = useState(null);
    
    const [formData, setFormData] = useState({
        code_categorie: '',
        nom_categorie: '',
        description: ''
    });

    useEffect(() => {
        fetchCategories();
    }, []);

    // Fonction pour afficher une alerte de succès
    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    // Fonction pour afficher une alerte d'erreur
    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/categories.php',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setCategories(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.code_categorie || !formData.nom_categorie) {
            showError("Le code et le nom de la catégorie sont requis");
            return;
        }

        setSaving(true);
        setError(null);

        try {
            const token = localStorage.getItem("token");
            let url = 'https://sawd.julesferry-ga.com/api/categories.php';
            let method = 'POST';
            
            if (modalMode === 'edit') {
                url += `?id=${selectedCategorie.id_categorie}`;
                method = 'PUT';
            }
            
            const response = await fetch(url, {
                method: method,
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            const result = await response.json();

            if (result.success) {
                setShowModal(false);
                fetchCategories();
                showSuccess(modalMode === 'add' ? 'Catégorie créée avec succès' : 'Catégorie mise à jour avec succès');
            } else {
                showError(result.message || "Erreur lors de l'enregistrement");
            }
        } catch (err) {
            showError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteClick = (id, nom) => {
        setCategorieToDelete({ id, nom });
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!categorieToDelete) return;
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/categories.php?id=${categorieToDelete.id}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            const result = await response.json();
            
            if (result.success) {
                setShowDeleteConfirm(false);
                setCategorieToDelete(null);
                fetchCategories();
                showSuccess("Catégorie supprimée avec succès");
            } else {
                showError(result.message || "Erreur lors de la suppression");
                setShowDeleteConfirm(false);
            }
        } catch (err) {
            showError("Erreur de connexion");
            setShowDeleteConfirm(false);
        }
    };

    const handleEdit = (categorie) => {
        setSelectedCategorie(categorie);
        setFormData({
            code_categorie: categorie.code_categorie,
            nom_categorie: categorie.nom_categorie,
            description: categorie.description || ''
        });
        setModalMode('edit');
        setShowModal(true);
    };

    const handleView = (categorie) => {
        setSelectedCategorie(categorie);
        setModalMode('view');
        setShowModal(true);
    };

    const handleAdd = () => {
        setFormData({
            code_categorie: '',
            nom_categorie: '',
            description: ''
        });
        setModalMode('add');
        setShowModal(true);
    };

    const filteredCategories = categories.filter(c => 
        c.code_categorie?.toLowerCase().includes(filterText.toLowerCase()) ||
        c.nom_categorie?.toLowerCase().includes(filterText.toLowerCase()) ||
        c.description?.toLowerCase().includes(filterText.toLowerCase())
    );

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* Alertes globales */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4">
                            <i className="ri-checkbox-circle-line me-2"></i>
                            {successMessage}
                        </Alert>
                    )}
                    
                    {showErrorAlert && (
                        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible className="mb-4">
                            <i className="ri-error-warning-line me-2"></i>
                            {errorMessage}
                        </Alert>
                    )}

                    {/* En-tête */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <ol className="breadcrumb fs-sm mb-1">
                                        <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
                                        <li className="breadcrumb-item"><Link to="/stock">Stock</Link></li>
                                        <li className="breadcrumb-item"><Link to="/stock/produits">Produits</Link></li>
                                        <li className="breadcrumb-item active">Catégories</li>
                                    </ol>
                                    <h4 className="main-title mb-0">
                                        <i className="ri-price-tag-3-line text-primary me-2"></i>
                                        Gestion des catégories
                                    </h4>
                                </div>
                                <Button variant="primary" onClick={handleAdd}>
                                    <i className="ri-add-line me-2"></i>
                                    Nouvelle catégorie
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Filtres */}
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <InputGroup>
                                        <InputGroup.Text>
                                            <i className="ri-search-line"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher catégorie..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                        />
                                    </InputGroup>
                                </Col>
                                <Col md={6} className="text-end">
                                    <span className="text-muted">
                                        {filteredCategories.length} catégorie(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des catégories */}
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Code</th>
                                            <th>Nom</th>
                                            <th>Description</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="4" className="text-center py-4">
                                                    <Spinner animation="border" variant="primary" />
                                                </td>
                                            </tr>
                                        ) : filteredCategories.length > 0 ? (
                                            filteredCategories.map((categorie) => (
                                                <tr key={categorie.id_categorie} className="categorie-row">
                                                    <td>
                                                        <Badge bg="light" text="dark" className="font-mono">
                                                            {categorie.code_categorie}
                                                        </Badge>
                                                    </td>
                                                    <td className="fw-semibold">{categorie.nom_categorie}</td>
                                                    <td>{categorie.description || '-'}</td>
                                                    <td className="text-center">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <Button 
                                                                variant="outline-info" 
                                                                size="sm"
                                                                onClick={() => handleView(categorie)}
                                                            >
                                                                <i className="ri-eye-line"></i>
                                                            </Button>
                                                            <Button 
                                                                variant="outline-primary" 
                                                                size="sm"
                                                                onClick={() => handleEdit(categorie)}
                                                            >
                                                                <i className="ri-pencil-line"></i>
                                                            </Button>
                                                            <Button 
                                                                variant="outline-danger" 
                                                                size="sm"
                                                                onClick={() => handleDeleteClick(categorie.id_categorie, categorie.nom_categorie)}
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center py-4">
                                                    <i className="ri-price-tag-3-line fs-48 text-muted mb-3 d-block"></i>
                                                    <h5>Aucune catégorie trouvée</h5>
                                                    <Button 
                                                        variant="primary" 
                                                        onClick={handleAdd}
                                                        className="mt-2"
                                                    >
                                                        <i className="ri-add-line me-2"></i>
                                                        Créer une catégorie
                                                    </Button>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* Modal Catégorie */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalMode === 'add' && 'Nouvelle catégorie'}
                        {modalMode === 'edit' && 'Modifier la catégorie'}
                        {modalMode === 'view' && 'Détails de la catégorie'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        {modalMode === 'view' && selectedCategorie ? (
                            <div className="text-center py-3">
                                <div className="category-icon mx-auto mb-3">
                                    <i className="ri-price-tag-3-line text-primary fs-1"></i>
                                </div>
                                <h4>{selectedCategorie.nom_categorie}</h4>
                                <p className="text-muted mb-2">Code: {selectedCategorie.code_categorie}</p>
                                {selectedCategorie.description && (
                                    <p className="mb-0">{selectedCategorie.description}</p>
                                )}
                            </div>
                        ) : (
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Code <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formData.code_categorie}
                                            onChange={(e) => setFormData({...formData, code_categorie: e.target.value.toUpperCase()})}
                                            disabled={modalMode === 'edit'}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Nom <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formData.nom_categorie}
                                            onChange={(e) => setFormData({...formData, nom_categorie: e.target.value})}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label>Description</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={formData.description}
                                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Fermer
                        </Button>
                        {modalMode !== 'view' && (
                            <Button variant="primary" type="submit" disabled={saving}>
                                {saving ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Enregistrement...
                                    </>
                                ) : (
                                    modalMode === 'add' ? 'Créer' : 'Mettre à jour'
                                )}
                            </Button>
                        )}
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal de confirmation de suppression */}
            <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="text-white">
                        <i className="ri-alert-line me-2"></i>
                        Confirmer la suppression
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Êtes-vous sûr de vouloir supprimer la catégorie :</p>
                    <p className="fw-bold fs-5">{categorieToDelete?.nom}</p>
                    <p className="text-muted small">Cette action est irréversible.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={confirmDelete}>
                        Confirmer la suppression
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .categorie-row {
                    transition: all 0.2s ease;
                }
                .categorie-row:hover {
                    background: rgba(13, 110, 253, 0.05);
                }
                .category-icon {
                    width: 80px;
                    height: 80px;
                    background: rgba(13, 110, 253, 0.1);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .font-mono {
                    font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
                }
            `}</style>
        </React.Fragment>
    );
}